import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { PAGE_SIZES } from "@/lib/pageSize";
import { cn } from "@/lib/utils";
import { Select } from "./field";
import { Button } from "./button";
import { useTranslation } from "react-i18next";

/** Jadval (redesign v2): karta ichida, sarlavha kichik katta harf, qator hover -- yumshoq to'ldirish */
/**
 * Jadval joylashuvi (2026-10-18, chekka tekisligi): `card` -- o'z kartasi (sahifada yakka); `bare` -- karta ichida
 * (`CardBody` px-5): ramkasiz, chekka ustunlar karta matni bilan tekis; `flush` -- karta ichida chekkagacha (`CardBody
 * px-0 pb-0`): chekka ustunlar karta sarlavhasi (px-5) bilan tekis. Karta ichiga `card` qo'yilmaydi (ikki qavat ramka).
 */
export type TableVariant = "card" | "bare" | "flush";

const WRAP: Record<TableVariant, string> = {
  card: "rounded-card border border-border/60 bg-surface shadow-card",
  bare: "[&_th:first-child]:pl-0 [&_td:first-child]:pl-0 [&_th:last-child]:pr-0 [&_td:last-child]:pr-0 [&_tbody_tr:last-child>td]:border-b-0",
  flush: "[&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5 [&_tbody_tr:last-child>td]:border-b-0",
};

export function Table({ className, variant = "card", ...props }: HTMLAttributes<HTMLTableElement> & { variant?: TableVariant }) {
  return (
    <div className={cn("overflow-x-auto", WRAP[variant])}>
      <table className={cn("w-full border-collapse text-[13px]", className)} {...props} />
    </div>
  );
}

export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn("border-b border-line px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.05em] whitespace-nowrap text-muted uppercase", className)}
      {...props}
    />
  );
}

export function Td({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("border-b border-line px-4 py-3 align-middle", className)} {...props} />;
}

export function Tr({ className, clickable, ...props }: HTMLAttributes<HTMLTableRowElement> & { clickable?: boolean }) {
  // data-state=open: shu qatorda o'ng tugma menyusi ochiq
  return <tr className={cn("transition-colors duration-100", clickable && "cursor-pointer hover:bg-fill/60", "data-[state=open]:bg-accent-soft/40", className)} {...props} />;
}

export function Pagination({ page, pages, count, pageSize, onPage, onPageSize, label = "items", className }: {
  page: number;
  pages: number;
  count: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize?: (s: number) => void;
  label?: string;
  /** Jadval kartasi ostida px-4 (katak matni bilan tekis); karta ichida `flush` jadvaldan keyin -- "border-t border-line px-5" */
  className?: string;
}) {
  const { t } = useTranslation();
  const from = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(count, page * pageSize);
  const nums: (number | "…")[] = [];
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1) nums.push(p);
    else if (nums[nums.length - 1] !== "…") nums.push("…");
  }
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-[12px] text-muted", className)}>
      <div>
        {t("table.showing", { from, to, count })} {label === "items" ? "" : label}
      </div>
      <div className="flex items-center gap-3">
        {onPageSize && (
          <Select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} aria-label={t("table.rowsPerPage")} className="h-8 w-auto rounded-full">
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {t("table.rowsPerPage")}: {s}
              </option>
            ))}
          </Select>
        )}
        <div className="flex gap-1">
          <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t("actions.back")}>
            ‹
          </Button>
          {nums.map((n, i) =>
            n === "…" ? (
              <span key={`e${i}`} className="px-2 leading-8">
                …
              </span>
            ) : (
              <Button key={n} size="sm" variant={n === page ? "primary" : "secondary"} onClick={() => onPage(n)}>
                {n}
              </Button>
            ),
          )}
          <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t("actions.next")}>
            ›
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FilterChips<T extends string>({ items, value, onChange }: { items: { key: T; label: ReactNode; count?: number }[]; value: T; onChange: (k: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className={cn(
              "h-8 rounded-full px-3.5 text-[12px] font-semibold whitespace-nowrap transition-colors duration-150",
              active ? "bg-ink text-surface" : "bg-fill text-ink-soft hover:bg-fill-hover hover:text-ink",
            )}
          >
            {it.label}
            {it.count !== undefined && <span className={cn("tabular ml-1.5", active ? "opacity-70" : "text-muted")}>{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
